import React, { ReactNode, useContext, useEffect, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageHeader from 'components/Layout/PageHeader'
import Card, { CardStatus } from 'components/Layout/Card'
import service from '../CampaignDetails/services/campaign-details.service'
import creationsService from '../CampaignForm/CreationsForm/CreationsFormPreview/service'
import MediaMap from 'components/MediaMap/MediaMap'
import CampaignDetailsRealization from '../CampaignDetails/CampaignDetailsRealization'
import CampaignDetailsSummary from '../CampaignDetails/CampaignDetailsSummary'
import CampaignDetailsCreations from '../CampaignDetails/CampaignDetailsCreations'
import FillingSpinner from 'components/FillingSpinner'
import CampaignDetailsChart from '../CampaignDetails/CampaignDetailsChart'
import useFilteredMedia from 'hooks/useFilteredMedia'
import CardHeader from 'components/Layout/CardHeader'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import {
  faClock,
  faCopy,
  faEdit,
  faFilePdf,
  faShare,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import Modal from 'components/Modal'
import CampaignDetailsShareUrl from '../CampaignDetails/CampaignDetailsShareUrl'
import { Routes } from 'routes'
import {
  Alert,
  CampaignAcceptanceStatus,
  CampaignBudgetCurrency,
  CampaignDetails as CampaignDetailsType,
  CampaignStatus,
  CampaignTarget,
  Creation,
  CreationAcceptance,
  MediaFormat,
  Medium,
} from 'types/campaign'
import { AppContext } from 'contexts/AppContext'
import { CAMPAIGN, CAMPAIGN_ACTION, PAGES } from 'constant/authorization'
import VariableUtils from 'utils/variable'
import { Features } from 'constant/features'
import CampaignDetailsAlerts from '../CampaignDetails/CampaignDetailsAlerts'
import CampaignDetailsActionModals, {
  DispatchModalAction,
  ModalActionKind,
  modalsReducer,
  modalsReducerInitialState,
} from '../CampaignDetails/CampaignDetailsActionModals'
import CampaignDetailsPdf from '../CampaignDetails/pdfs/CampaignDetailsPdf'
import { pdf } from '@react-pdf/renderer'
import DownloadUtils from 'utils/download'
import CampaignDetailsMediaList from '../CampaignDetails/CampaignDetailsMediaList'
import ArrayUtils from 'utils/array'
import CampaignDetailsNote from '../CampaignDetails/CampaignDetailsNote'
import CampaignDetailsHistory from '../CampaignDetails/CampaignDetailsHistory'
import '../CampaignDetails/CampaignDetails.scss'
import { useDispatch, useSelector } from 'react-redux'
import { mapActions, MapSliceModel } from '../../../components/Map/store/map-slice'
import AcceptancesCard from '../../../components/CreationAcceptancesCard'
import useCampaignAction from '../../../hooks/useCampaignAction'
import { BaseRole } from '../../../types/user'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons/faPaperPlane'
import { CampaignData } from '../models/campaign-data.model'
import { CampaignPermissionType, FeatureName } from 'types/features'
import { ActionButtonsHelper } from '../../../utils/action-buttons-helper'
import CampaignStatusComponent from '../../EmissionPlan/components/CampaignStatus'
import GoogleAnalyticsHelper from '../../../utils/google-analytics/google-analytics-helper'
import { TrackingActions } from '../../../utils/google-analytics/tracking-actions'
import api from 'api'
import EnumUtils from 'utils/enum'
import { GetCampaignForDetailsData } from 'api/campaign/types'

interface CampaignDetailsTabProps {
  campaignId?: string
  campaignUuid?: string
  publicView?: boolean
  prefetchedCampaignData?: GetCampaignForDetailsData
}

const CampaignDetailsTab: React.FC<CampaignDetailsTabProps> = ({
  campaignId,
  campaignUuid,
  publicView,
  prefetchedCampaignData,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { allowedFor, isFeatureActive, userData } = useContext(AppContext)
  const navigate = useNavigate()

  const [campaignData, setCampaignData] = useState<CampaignData | null>(null)
  const [campaignDetails, setCampaignDetails] = useState<CampaignDetailsType | null>(null)
  const [displayedMedia, setDisplayedMedia] = useState<Medium[]>([])

  const [shouldReload, setShouldReload] = useState<boolean>(false)
  const [isMediaListCollapsed, setIsMediaListCollapsed] = useState<boolean>(true)
  const [isMapCardCollapsed, setIsMapCardCollapsed] = useState<boolean>(false)
  const [isShareUrlModalOpen, setIsShareUrlModalOpen] = useState<boolean>(false)
  const { fetchCampaignMedia, mediaLoading, media } = useFilteredMedia()
  const mapLoading = useSelector(state => (state as { map: MapSliceModel }).map.loading)
  const [acceptancesList] = useState<CreationAcceptance[]>([])
  const [loadingAcceptanceList, setLoadingAcceptanceList] = useState<boolean>(false)

  const [modalsState, dispatchModalAction] = useReducer(modalsReducer, modalsReducerInitialState)
  const { sendCampaignToAcceptance } = useCampaignAction()

  const dispatchModal: DispatchModalAction = (type, payload) =>
    dispatchModalAction({ type, payload })

  const processCampaignRawData = (rawData: GetCampaignForDetailsData): void => {
    const data: CampaignData = {
      alerts: (rawData.alerts || []).filter((a: Alert) => a.active),
      acceptanceAlerts: rawData.acceptanceAlerts || [],
      aggregatedAcceptanceAlerts: rawData.aggregatedAcceptanceAlerts || [],
      brainState: rawData.brainState,
      briefName: rawData.briefName,
      budget: rawData.budget,
      budgetCurrency:
        CampaignBudgetCurrency[
          EnumUtils.getKeyByValue(
            CampaignBudgetCurrency,
            rawData.budgetCurrency
          ) as keyof typeof CampaignBudgetCurrency
        ],
      creations: rawData.creations.map((c: Creation) => ({
        ...c,
        mediaFormat: MediaFormat[c.mediaFormat as unknown as keyof typeof MediaFormat],
      })),
      agencyName: rawData.agency.name,
      agencyType: rawData.agency.agencyType,
      agencyWithAllocatedTime: rawData.agency.withAllocatedTime,
      billingAllowance: rawData.agency.billingAllowance,
      endDate: rawData.endDate,
      id: rawData.id,
      impressions: rawData.impressions,
      emissionType: rawData.emissionType,
      mediaCriteriaSearch: {
        ...rawData.mediaCriteriaSearch,
        mediumFormat:
          MediaFormat[
            rawData.mediaCriteriaSearch.mediumFormat as unknown as keyof typeof MediaFormat
          ],
      },
      commercialAttribute: rawData.commercialAttribute,
      name: rawData.name,
      note: rawData.note,
      contractNote: rawData.contractNote,
      offer: rawData.offer,
      priority: rawData.priority,
      reservationTill: rawData.reservationTill ? new Date(rawData.reservationTill) : null,
      startDate: rawData.startDate,
      status:
        CampaignStatus[
          EnumUtils.getKeyByValue(CampaignStatus, rawData.status) as keyof typeof CampaignStatus
        ],
      supervisors: rawData.supervisors || [],
      target:
        CampaignTarget[
          EnumUtils.getKeyByValue(CampaignTarget, rawData.target) as keyof typeof CampaignTarget
        ],
      acceptanceStatus: rawData.acceptanceStatus,
      uuid: rawData.uuid,
      currentValue: rawData.currentValue,
      estimatedValue: rawData.estimatedValue,
      estimatedValueParameters: rawData.estimatedValueParameters,
      createdAt: rawData.createdAt,
      mediaDuration: rawData.mediaDuration,
      audience: rawData.audience,
      bundle: rawData.bundle,
      dealId: rawData.dealId,
      creationContributionType: rawData.creationContributionType,
      campaignTagList: rawData.campaignTagList,
    }
    setCampaignData(data)
  }

  // Fetch campaign data
  useEffect(() => {
    if (publicView && prefetchedCampaignData) {
      processCampaignRawData(prefetchedCampaignData)
    } else if (publicView && campaignUuid) {
      api.campaign
        .getCampaignForDetailsPublic(campaignUuid)
        .then(res => processCampaignRawData(res.data.campaignReportPublic.campaign))
        .catch(() => {
          console.log('error')
        })
    } else if (campaignId) {
      api.campaign
        .getCampaignForDetails(campaignId)
        .then(res => processCampaignRawData(res.data.node))
        .catch(() => {
          console.log('error')
        })
    }
  }, [campaignId, campaignUuid, prefetchedCampaignData, shouldReload])

  // Fetch campaign details
  const processCampaignDetailsData = (data: any): void => {
    setCampaignDetails({
      ...data,
      target:
        CampaignTarget[
          EnumUtils.getKeyByValue(CampaignTarget, data.target) as keyof typeof CampaignTarget
        ],
      everUsedMedia: data.everUsedMedia?.nodes || [],
      currentEmissions: data.currentImpressions,
      totalEmissions: data.totalImpressions,
      totalHiddenEmissions: data.totalHiddenImpressions,
      guaranteedEmissions: data.guaranteedImpressions,
    })
  }

  // Fetch campaign details
  useEffect(() => {
    if (publicView && campaignUuid) {
      api.campaign
        .getCampaignDetailsPublic(campaignUuid)
        .then(res => processCampaignDetailsData(res.data.campaignReportPublic.campaignDetails))
        .catch(() => {
          console.log('error')
        })
    } else if (campaignId) {
      api.campaign
        .getCampaignDetails(campaignId)
        .then(res => processCampaignDetailsData(res.data.campaignDetails))
        .catch(() => {
          console.log('error')
        })
    }
  }, [campaignId, campaignUuid, shouldReload])

  useEffect(() => {
    if (!media || !campaignDetails || !campaignDetails.everUsedMedia) {
      return
    }

    const displayed: Medium[] = ArrayUtils.unique(
      media.concat(campaignDetails?.everUsedMedia || []),
      (m1, m2) => m1.id === m2.id
    )

    setDisplayedMedia(displayed)
  }, [media, campaignDetails])

  useEffect(() => {
    if (campaignData) {
      fetchCampaignMedia(
        publicView ? { campaignUuid: campaignData.uuid } : { campaignId: campaignData.id }
      )
    }

    if (
      !publicView &&
      campaignData?.id &&
      (campaignData.mediaCriteriaSearch.mediumFormat === MediaFormat.DG_IN ||
        campaignData.mediaCriteriaSearch.mediumFormat === MediaFormat.DG_CS)
    ) {
      setLoadingAcceptanceList(true)
      creationsService
        .getAcceptancesList(campaignData.id)
        .then(() => setLoadingAcceptanceList(false))
    }
  }, [campaignData])

  const Header = (campaign: CampaignData): ReactNode => {
    const status = campaign.status
    const mediumFormat = campaign.mediaCriteriaSearch.mediumFormat
    const acceptanceStatus = campaign.acceptanceStatus
    const reservationTill = campaign.reservationTill
    const isDataForPdfFetched = service.isDataForPdfFetched(
      campaign,
      campaignDetails ?? undefined,
      mediaLoading
    )

    const navigateToEditCampaign = () => {
      if (status === CampaignStatus.ACTIVE) {
        dispatchModal(ModalActionKind.EDIT_OPEN, true)
        return
      }

      void navigate(Routes.CAMPAIGNS.EDIT(campaign.id))
    }

    const navigateToEmissionPlan = () => {
      void navigate(Routes.EMISSION_PLAN(campaign.uuid))
    }

    const canAdminEditCampaign =
      !allowedFor({
        template: CAMPAIGN_ACTION.EDIT_IN_PENDING_ACCEPTANCE_NOT_BY_CLIENT,
        feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Write },
        status,
      }) && campaignData?.acceptanceStatus === CampaignAcceptanceStatus.pending_acceptation

    const canClientEditCampaign =
      allowedFor({
        template: CAMPAIGN_ACTION.EDIT_ACTIVE_PLANNED_RESERVED_CAMPAIGN_BY_CLIENT,
        status,
        feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Write },
      }) && !userData.user?.roles.some(role => role.baseRole === BaseRole.ADMIN)

    const canSendToAcceptance = ActionButtonsHelper.getCanSendToAcceptance(
      allowedFor,
      status,
      userData.withAllocatedTime,
      false
    )

    const canFinish = ActionButtonsHelper.getCanFinish(
      allowedFor,
      status,
      userData.withAllocatedTime
    )

    return (
      <div className='CampaignDetails__header--container'>
        <PageHeader className='CampaignDetails__header'>
          {campaign.name}
          <div className='CampaignDetails__header--buttons'>
            {!publicView && canSendToAcceptance && (
              <Button
                disabled={
                  campaignData?.acceptanceStatus === CampaignAcceptanceStatus.pending_acceptation ||
                  campaignData?.creations.length === 0
                }
                dataCy={'send-to-acceptance-btn'}
                theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                size={ButtonSize.SMALL}
                icon={faPaperPlane}
                onClick={() => void sendCampaignToAcceptance(campaign.id)}
              >
                {t('common.sendToAcceptance')}
              </Button>
            )}

            {!publicView && allowedFor({ template: CAMPAIGN_ACTION.COPY, mediumFormat }) && (
              <Button
                dataCy={'copy-campaign-btn'}
                theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                size={ButtonSize.SMALL}
                icon={faCopy}
                onClick={() => {
                  dispatchModal(ModalActionKind.CREATE_COPY_OPEN, true)
                }}
              >
                {t('common.createCopy')}
              </Button>
            )}

            {!publicView &&
              allowedFor({
                template: CAMPAIGN_ACTION.EDIT,
                feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Write },
                status,
              }) && (
                <Button
                  theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                  size={ButtonSize.SMALL}
                  icon={faEdit}
                  disabled={
                    (canAdminEditCampaign || canClientEditCampaign) &&
                    !ActionButtonsHelper.getCanEditByOwner(status, userData.withAllocatedTime)
                  }
                  onClick={navigateToEditCampaign}
                >
                  {t('common.edit')}
                </Button>
              )}

            {!publicView && allowedFor({ template: CAMPAIGN_ACTION.DOWNLOAD, status }) && (
              <Button
                theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                size={ButtonSize.SMALL}
                icon={faFilePdf}
                onClick={async () => {
                  GoogleAnalyticsHelper.trackEvent(TrackingActions.CAMPAIGN_DETAILS_POSTBUY_CLICK)

                  if (!isDataForPdfFetched) {
                    return
                  }

                  if (campaign.target === CampaignTarget.IMPRESSIONS) {
                    dispatch(mapActions.setLoading(true))
                    pdf(
                      <CampaignDetailsPdf
                        data={{ ...campaign, ...campaignDetails!, media: displayedMedia }}
                      />
                    )
                      .toBlob()
                      .then(blob => DownloadUtils.downloadBlob(blob, `${campaign.name}.pdf`))
                      .then(() => dispatch(mapActions.setLoading(false)))

                    return
                  }

                  dispatch(mapActions.setLoading(true))

                  const createAction = await service.createCampaignSummaryPdf(campaign.uuid)

                  if (!createAction || !createAction.success) {
                    dispatch(mapActions.setLoading(false))
                    return
                  }

                  const jobId = createAction.task.jobId

                  const interval = setInterval(async () => {
                    const fetchAction = await service.fetchPdfTaskStatus(jobId)

                    if (!fetchAction || fetchAction.status === 'failed') {
                      clearInterval(interval)
                      dispatch(mapActions.setLoading(false))
                      return
                    }

                    if (fetchAction.file) {
                      clearInterval(interval)
                      dispatch(mapActions.setLoading(false))

                      await DownloadUtils.downloadFileFromUrl(
                        fetchAction.file,
                        `${campaign.name}.pdf`
                      )
                      return
                    }
                  }, 5000)

                  setTimeout(() => {
                    clearInterval(interval)
                    dispatch(mapActions.setLoading(false))
                  }, 180000)
                }}
                disabled={!isDataForPdfFetched || mapLoading}
              >
                {!mapLoading ? (
                  t('common.postbuy')
                ) : (
                  <FillingSpinner className='CampaignDetails__pdf-spinner' />
                )}
              </Button>
            )}

            <Button
              theme={ButtonTheme.NAVY_BLUE_OUTLINE}
              size={ButtonSize.SMALL}
              icon={faClock}
              onClick={navigateToEmissionPlan}
            >
              {t('common.emissionPlan')}
            </Button>

            {!publicView && (
              <>
                {allowedFor({ template: CAMPAIGN_ACTION.SHARE, status }) && (
                  <Button
                    theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                    size={ButtonSize.SMALL}
                    icon={faShare}
                    dataCy={'campaign-details-share-campaign-btn'}
                    onClick={() => void setIsShareUrlModalOpen(true)}
                  >
                    {t('common.share')}
                  </Button>
                )}
                {allowedFor({
                  feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Delete },
                  status,
                }) && (
                  <Button
                    theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                    size={ButtonSize.SMALL}
                    icon={faTrashAlt}
                    dataCy={'remove-campaign-button'}
                    onClick={() => void dispatchModal(ModalActionKind.REMOVE_OPEN, true)}
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </>
            )}
          </div>
        </PageHeader>

        <div className='CampaignDetails__header--second'>
          {CampaignStatusComponent({ status, acceptanceStatus, reservationTill })}

          {!publicView && (
            <>
              {allowedFor({
                template: CAMPAIGN_ACTION.PUBLISH,
                feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Publish },
                status,
              }) && (
                <Button
                  onClick={() => void dispatchModal(ModalActionKind.PUBLISH_OPEN, true)}
                  disabled={VariableUtils.isEmptyObject(campaign.brainState)}
                >
                  {t('common.publishChanges')}
                </Button>
              )}

              {ActionButtonsHelper.getCanApprove(
                allowedFor,
                status,
                userData.withAllocatedTime
              ) && (
                <Button onClick={() => void dispatchModal(ModalActionKind.APPROVE_OPEN, true)}>
                  {t('common.activateCampaign')}
                </Button>
              )}

              {allowedFor({
                template: CAMPAIGN_ACTION.APPROVE,
                status,
              }) &&
                acceptanceStatus === CampaignAcceptanceStatus.pending_acceptation && (
                  <Button
                    onClick={() => void dispatchModal(ModalActionKind.REJECT_OPEN, true)}
                    theme={ButtonTheme.RED_OUTLINE}
                  >
                    {t('common.rejectCampaign')}
                  </Button>
                )}

              {isFeatureActive(Features.PAUSE_CAMPAIGN) &&
                allowedFor({ template: CAMPAIGN_ACTION.PAUSE, status }) && (
                  <Button
                    theme={ButtonTheme.BLUE_OUTLINE}
                    onClick={() => void dispatchModal(ModalActionKind.PAUSE_OPEN, true)}
                  >
                    {t('campaignDetails.pause')}
                  </Button>
                )}

              {canFinish && (
                <Button
                  theme={ButtonTheme.BLUE_OUTLINE}
                  onClick={() => void dispatchModal(ModalActionKind.STOP_OPEN, true)}
                >
                  {t('campaignDetails.finish')}
                </Button>
              )}

              {allowedFor({
                template: CAMPAIGN_ACTION.CONFIRM_RESERVATION,
                status,
              }) && (
                <Button
                  theme={ButtonTheme.BLUE}
                  onClick={() => void dispatchModal(ModalActionKind.CONFIRM_RESERVATION_OPEN, true)}
                >
                  {t('reservation.confirmReservation')}
                </Button>
              )}

              {allowedFor({
                template: CAMPAIGN_ACTION.CANCEL_RESERVATION,
                status,
              }) && (
                <Button
                  theme={ButtonTheme.BLUE_OUTLINE}
                  onClick={() => void dispatchModal(ModalActionKind.CANCEL_RESERVATION_OPEN, true)}
                >
                  {t('reservation.cancelReservation')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const NoteCard = ({ note, contractNote }: CampaignData): ReactNode =>
    allowedFor({ template: CAMPAIGN.NOTE }) &&
    (note || contractNote) && (
      <CampaignDetailsNote
        note={note}
        contractNote={contractNote}
      />
    )

  const AlertsCard = (campaignData: CampaignData): ReactNode => {
    const combinedAlerts = [...campaignData.alerts, ...campaignData.aggregatedAcceptanceAlerts]

    return (
      allowedFor({ template: CAMPAIGN.ALERTS }) && <CampaignDetailsAlerts alerts={combinedAlerts} />
    )
  }

  const RealizationCard = (campaign: CampaignData): ReactNode => (
    <Card
      id='realization'
      title={t('campaignDetails.realization.header')}
      noMarginBody
    >
      <CampaignDetailsRealization
        campaign={campaign}
        publicView={!!publicView}
      />
      <CampaignDetailsChart
        publicViewProps={{ publicView: !!publicView, campaignUuid: campaign.uuid }}
      />
    </Card>
  )

  const SummaryCard = (campaignData: CampaignData, details: CampaignDetailsType): ReactNode => (
    <Card
      id='summary'
      title={t('common.campaignDetails')}
      noMarginBody
    >
      <CampaignDetailsSummary
        campaignData={campaignData}
        everUsedMediaCount={details.everUsedMedia.length}
        mediaCount={media.length}
        mediaLoading={mediaLoading}
        publicView={!!publicView}
      />
    </Card>
  )

  const CreationsCard = (campaignData: CampaignData): ReactNode => {
    const hasCreations = campaignData.creations.length > 0

    return (
      <Card
        id='creations'
        title={t('campaignDetails.creations')}
        noMarginBody={hasCreations}
      >
        {hasCreations ? (
          <CampaignDetailsCreations
            campaignData={{
              id: campaignData.id,
              startDate: campaignData.startDate,
              endDate: campaignData.endDate,
              status: campaignData.status,
              acceptanceStatus: campaignData.acceptanceStatus,
              mediaFormat: campaignData.mediaCriteriaSearch.mediumFormat,
              impressions: campaignData.impressions,
              target: campaignData.target,
              audience: campaignData.audience,
              creationContributionType: campaignData.creationContributionType,
            }}
            creations={campaignData.creations}
            publicViewProps={{ publicView: !!publicView, campaignUuid: campaignData.uuid }}
            showAllMediaFormats={false}
          />
        ) : (
          <div className='CampaignDetails__no-creations-container'>
            {t('campaignDetails.noCreationsInfo')}
          </div>
        )}
      </Card>
    )
  }

  const AcceptancesCardTable = (
    acceptances: CreationAcceptance[],
    creations?: Creation[]
  ): ReactNode => {
    return (
      <AcceptancesCard
        campaignId={campaignId}
        acceptances={acceptances}
        loadingAcceptances={loadingAcceptanceList}
        creations={creations}
        isCollapsible={true}
      ></AcceptancesCard>
    )
  }

  const ShareUrlModal = (c: CampaignData): ReactNode =>
    isShareUrlModalOpen &&
    c.uuid && (
      <Modal
        title={t('campaignDetails.shareUrl.title')}
        isOpen={isShareUrlModalOpen}
        onClose={(isOpen: boolean) => void setIsShareUrlModalOpen(isOpen)}
        footer={
          <Button onClick={() => void setIsShareUrlModalOpen(false)}>{t('common.finished')}</Button>
        }
      >
        <CampaignDetailsShareUrl
          url={window.location.origin + Routes.CAMPAIGNS.ONE_PUBLIC(c.uuid)}
        />
      </Modal>
    )

  const MapCard = (): ReactNode =>
    displayedMedia.length > 0 && (
      <Card
        id='map-card'
        className='CampaignDetails__map--card'
        title={t('common.placementOfMedia')}
        status={mediaLoading ? CardStatus.LOADING : CardStatus.NONE}
        isCollapsed={isMapCardCollapsed}
        onCollapse={collapsed => void setIsMapCardCollapsed(collapsed)}
        noMarginBody
      >
        {!isMapCardCollapsed && (
          <MediaMap
            className='CampaignDetails__map'
            media={displayedMedia}
            withClustering
          />
        )}
      </Card>
    )

  const MediaListCard = (campaignData: CampaignData): ReactNode => (
    <Card>
      <CardHeader
        collapsed={isMediaListCollapsed}
        onCollapse={(collapsed: boolean) => void setIsMediaListCollapsed(collapsed)}
      >
        {t('common.mediaList')}
      </CardHeader>
      {!isMediaListCollapsed && (
        <CampaignDetailsMediaList
          mediumFormat={campaignData.mediaCriteriaSearch.mediumFormat}
          media={displayedMedia}
          publicView={!!publicView}
        />
      )}
    </Card>
  )

  const HistoryCard = (campaign: CampaignData) =>
    !publicView &&
    allowedFor({ template: PAGES.CAMPAIGNS.HISTORY }) && (
      <CampaignDetailsHistory campaign={campaign} />
    )

  const cards = (campaign: CampaignData) => (
    <>
      {Header(campaign)}
      {!publicView && NoteCard(campaign)}
      {!publicView && AlertsCard(campaign)}
      {RealizationCard(campaign)}
      {campaignDetails && SummaryCard(campaign, campaignDetails)}
      {CreationsCard(campaign)}
      {!publicView &&
        acceptancesList.length > 0 &&
        (campaignData?.mediaCriteriaSearch.mediumFormat === MediaFormat.DG_IN ||
          campaignData?.mediaCriteriaSearch.mediumFormat === MediaFormat.DG_CS) &&
        AcceptancesCardTable(acceptancesList, campaignData?.creations)}
      {MapCard()}
      {MediaListCard(campaign)}
      {!publicView && ShareUrlModal(campaign)}
      {HistoryCard(campaign)}
    </>
  )

  return campaignData ? (
    <div className='CampaignDetails'>
      {cards(campaignData)}

      {!publicView && (
        <CampaignDetailsActionModals
          campaign={{ id: campaignData.id, name: campaignData.name }}
          onShouldReload={setShouldReload}
          reducerState={modalsState}
          dispatch={dispatchModal}
        />
      )}
    </div>
  ) : (
    <FillingSpinner />
  )
}

export default CampaignDetailsTab
